// Shared between the user story wizard (pages/story.tsx) and the admin
// gallery-story generator (pages/admin/gallery-stories.tsx) — one list keeps
// both flows offering the exact same creative options, so data entered
// through either one is directly comparable.

// A spark for anyone staring at a blank "Custom" box. Tapping one drops it
// straight into the idea field — pick it, tweak it, or use it as-is.
export const IDEA_SPARKS = [
    'A rocket made of candy crash-lands in the back garden',
    'A shy dragon who is scared of the dark discovers a torch that glows with kindness',
    'A tiny door appears at the bottom of the wardrobe, leading to a kingdom of talking teacups',
    'A grumpy raincloud learns how to make it rain rainbows instead of grey rain',
    'A treasure map turns out to be hidden inside a birthday cake',
    'The stars go missing one night and only one brave child can find them',
    'A pet hamster turns out to be a secret superhero once the lights go out',
    'A magic paintbrush brings every drawing to life for just one day',
    'A lost sock leads on an adventure through the whole laundry-basket world',
    'A robot who has never felt an emotion learns to laugh for the first time',
    'A very unusual new leader is elected by a school of fish',
    'A snowman wishes upon a star to see summer just once',
    'A mysterious seed grows into a beanstalk leading to a city in the clouds',
    'A shadow starts moving on its own and needs help finding its way home',
    'A tiny mouse becomes the bravest knight in the whole castle',
    'A jar of fireflies turns out to hold wishes that got trapped inside',
    'A paper boat sails out of the bathtub and into a whole ocean of adventure',
    'The moon loses its glow and asks for help getting it back',
    'A backyard vegetable patch turns out to be hiding a village of tiny gardeners',
    'A library book won\'t stop following its reader home',
    'A kite gets tangled in a cloud and needs rescuing before the storm',
    'An umbrella turns out to be enchanted and only opens over someone who needs cheering up',
    'A grandparent\'s old music box plays a tune that opens a door to their childhood',
    'A puddle after the rain turns out to be a doorway to an upside-down city',
    'A talking compass only points toward wherever kindness is needed most',
    'A carnival ride keeps going after the fair has closed, all through the night',
    'A pair of odd wellington boots let their wearer walk on water',
    'A neighbourhood cat turns out to be the guardian of a secret garden',
    'A balloon that never pops carries its owner higher than they ever imagined',
    'The last leaf of autumn refuses to fall until it finds someone to say goodbye to',
];

export const randomSpark = () => IDEA_SPARKS[Math.floor(Math.random() * IDEA_SPARKS.length)];

// Same idea, for anyone staring at a blank hero description — a little
// visual/personality colour to start from instead of a blinking cursor.
export const DESC_SPARKS = [
    'Curly brown hair, loves dinosaurs and the colour yellow',
    'Freckles and a big gap-toothed smile, obsessed with space and astronauts',
    'Short black hair in two tiny buns, never goes anywhere without a stuffed rabbit',
    'Bright red glasses, dreams of being a deep-sea explorer',
    'Long blonde pigtails, never seen without a home-made superhero cape',
    'A buzz cut and a missing front tooth, loves anything with wheels',
    'Braids with colourful beads, collects shiny rocks and seashells',
    'Round glasses and a wide grin, wants to be a wizard when they grow up',
    'A curly afro, always humming a made-up tune',
    'Straight dark hair and a dimple, loves drawing dragons in every notebook',
    'Freckled cheeks, obsessed with dinosaurs and jumping in puddles',
    'Wavy ginger hair, carries a magnifying glass everywhere like a detective',
    'A gap between the front teeth, loves collecting bugs in jam jars',
    'A bright green raincoat and yellow boots, adores rainy days',
    'Two missing front teeth, giggles at almost everything',
    'Loves capes, masks and jumping off the couch to save the day',
    'Always in dinosaur pyjamas, roars at anyone who will listen',
    'Big brown eyes and a mop of curls, scared of the dark but brave in daylight',
    'Loves unicorns, glitter and anything that sparkles',
    'A mountain of stuffed animals and a gap-toothed grin',
    'Tiny denim overalls, never without a favourite toy truck',
    'Loves the ocean, mermaids and collecting sea glass',
    'A cowlick that never lies flat, mad about trains',
    'Big round cheeks, giggles easily, loves chasing bubbles',
    'Short curly hair, a big fan of knights, castles and dragons',
    'A gappy smile and a favourite dinosaur backpack',
    'Loves the stars, the moon, and asking a hundred questions about space',
];

export const randomDesc = () => DESC_SPARKS[Math.floor(Math.random() * DESC_SPARKS.length)];

/** A different-looking `count`-sized sample each time `seed` changes — no two Shuffle taps in a row repeat the same set. */
export const shuffledSample = <T,>(items: T[], seed: number, count: number): T[] => {
    const arr = [...items];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = (i * 31 + seed * 17) % (i + 1);
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.slice(0, count);
};

// One curated pool of ideas per story theme (matches the theme names in
// config('imagerie.story_types'), minus 'Custom' — Custom keeps using
// IDEA_SPARKS above as its pool). Picking a theme shows these instead of the
// generic list, so the suggestion actually fits what was chosen.
export const THEME_IDEAS: Record<string, string[]> = {
    Adventure: [
        'A map found in an old attic leads to a treasure buried under the school playground',
        'A friendly giant asks for help finding their way back over the mountains',
        'A tiny raft ride down the garden stream turns into a journey through five different lands',
        'A compass that only points toward adventure leads into a forest nobody has ever explored',
        'A hidden cave behind a waterfall turns out to be the entrance to a whole underground kingdom',
        'An old rope bridge creaks open a path to a valley where the animals can talk',
        'A mysterious key found on the beach unlocks a door built into the cliffside',
    ],
    Bedtime: [
        'The moon asks for help tucking in all the stars before morning',
        'A sleepy cloud drifts by each night, collecting one good dream from every rooftop',
        'A cosy blanket fort turns out to be a doorway into the land of dreams',
        'A gentle night-light firefly guides the way to a meadow where everyone naps together',
        'The last yawn of the day wakes a family of sleepy owls who need help finding their nest',
        'A lullaby hummed at bedtime is the only thing that can calm a very tired baby dragon',
        'A soft glowing lantern leads a sleepy fox home past a whole town settling in for the night',
    ],
    'Fairy Tale': [
        'A forgotten castle in the woods wakes up the moment someone says its name out loud',
        "A humble baker's child discovers they're the last in a long line of gentle-hearted royals",
        'A talking mirror only shows the way home to whoever is brave enough to be kind',
        'An enchanted garden blooms only when someone tells it the truth',
        "A dragon guarding a tower turns out to just be lonely and wants a friend, not a fight",
        'A glass slipper left behind after a ball leads on a quest to find its rightful owner',
        'A fairy godmother grants one wish, but it comes with a very unexpected lesson',
    ],
    Educational: [
        'A shrinking potion lets the hero explore how a tiny seed grows into a giant tree',
        'A magical school bus ride takes the class on a journey through the water cycle',
        'A curious mouse learns how bees make honey, one flower at a time',
        'A trip inside a see-through submarine teaches the hero about ocean life, layer by layer',
        'A talking calendar helps the hero learn the months and seasons by visiting each one',
        'A friendly robot explains how the solar system works using marbles from the toy box',
        'A visit to a working farm shows the hero exactly where their breakfast comes from',
    ],
    Superhero: [
        'An ordinary cape found in a costume box turns out to have real powers — but only when used to help others',
        'A young hero discovers their superpower is turning fear into courage for everyone around them',
        'A city of tiny problems (a stuck kite, a lost puppy) needs a brand-new hero with a big heart',
        'A superhero training academy for kids teaches that the greatest power of all is kindness',
        'A sidekick pet reveals it has been secretly saving the day all along',
        "A superhero loses their powers for a day and learns that helping doesn't need superpowers at all",
        'A mysterious glowing badge chooses a new hero to protect the neighbourhood for one special day',
    ],
    'Animal Friends': [
        'A shy new animal moves into the forest and needs help making its first friend',
        'A grumpy old tortoise and an energetic young rabbit learn they make a great team',
        'A flock of birds needs help planning the perfect migration party',
        'A lost baby elephant is guided home by a chain of animal friends passing the message along',
        'A wise old owl teaches the forest animals how to solve an argument without fighting',
        'A tiny ant proves that the smallest friend can still make the biggest difference',
        "A group of animals who don't normally get along must work together to save their shared home",
    ],
    Space: [
        'A homemade rocket built from cardboard boxes somehow really blasts off into space',
        'A friendly alien lands in the backyard looking for a new best friend',
        'A shooting star grants one wish, but only to someone brave enough to chase it across the galaxy',
        "A trip to the moon reveals it's actually made of something far sillier than cheese",
        'A lost robot from another planet needs help finding its way back to its spaceship',
        'The stars go quiet one night and only a curious child notices something is wrong',
        'A telescope shows a whole new constellation that spells out a secret message',
    ],
    Mystery: [
        'Muddy footprints leading into the garden shed need to be traced back to their owner',
        'A missing favourite toy sets off a clue-by-clue treasure hunt around the whole house',
        'Strange noises coming from the attic turn out to have a surprisingly friendly explanation',
        'A secret message written in disappearing ink needs to be solved before it fades for good',
        'Someone has been leaving small gifts on the doorstep every morning, and no one knows who',
        'A locked box found under the floorboards holds a puzzle passed down through generations',
        "The cookies keep disappearing from the jar, and the culprit is closer than anyone thinks",
    ],
    Birthday: [
        'A birthday wish accidentally comes true in the silliest possible way',
        'A surprise party is being planned, but everyone keeps almost giving the secret away',
        "A magic balloon grants one wish for every year of the birthday child's age",
        "The birthday cake goes missing just before the party, and there's no time to lose",
        'A birthday card someone forgot to send opens a door to a party from long ago',
        'A very shy guest at the party turns out to be the most important one there',
        'Every gift at the party turns out to be a clue leading to one last, best surprise',
    ],
    Underwater: [
        'A coral reef is fading, and only a brave young fish can find the ancient song to bring back its colour',
        'A shipwreck full of old treasure becomes a cosy new home for an octopus family',
        'A tiny seahorse gets separated from its school and needs help finding its way back',
        'A friendly dolphin offers a ride through an underwater city built entirely of coral',
        'A pearl found deep on the ocean floor turns out to hold a tiny, glowing secret',
        'A submarine adventure reveals a hidden cave lit by thousands of glowing jellyfish',
        'A young mermaid discovers a way to help sea creatures and humans understand each other',
    ],
    Dinosaurs: [
        'A dinosaur egg hatches in the backyard and needs help learning how to be brave',
        'A gentle long-necked dinosaur is too big for its usual watering hole and needs a friend\'s help',
        'A time-travelling fossil dig transports the hero back to a valley full of friendly dinosaurs',
        'A baby triceratops gets lost from its herd during a thunderstorm',
        "A dinosaur who's afraid of loud roars learns that being different is nothing to be scared of",
        'A volcano rumbling in the distance sends the whole prehistoric valley on a journey to safety together',
        "A tiny dinosaur believes it's too small to help anyone, until a big problem proves otherwise",
    ],
    Friendship: [
        'Two children who seem to have nothing in common discover they both love the same secret hideout',
        'A new kid at school just needs one person brave enough to say hello first',
        'A small disagreement between best friends needs a clever, kind way to be made right again',
        'A shy child finds the courage to make a new friend by sharing something they made themselves',
        'Two rival treehouse clubs learn they\'re stronger as one big friendship',
        'A friend moving away teaches the hero that friendship can stretch across any distance',
        'A quiet new animal at the park slowly learns to trust the friends who keep including it',
    ],
    'Time Travel': [
        'An old pocket watch found in the attic turns out to tick backwards in time',
        'A hero travels back to meet their grandparent as a child and learns a treasured family story',
        'A time machine built from a cardboard box accidentally works on its very first try',
        'A trip to the future reveals a message left specially for the hero to find',
        'A photograph starts glowing and pulls the hero straight into the moment it was taken',
        'A hero visits their own younger self to share one important piece of advice',
        'A clock tower in town secretly holds the key to a doorway through different decades',
    ],
    'Sports & Adventure Games': [
        'A nervous new player discovers that practice, not perfection, is what makes a great teammate',
        'An underdog team learns that working together beats any single superstar',
        'A backyard obstacle course becomes a full-blown adventure with unexpected challenges',
        'A big game is coming up, and the hero must learn to turn butterflies into excitement',
        'A friendly rivalry between two young athletes turns into an unlikely friendship',
        'A lost team mascot needs to be found before the biggest game of the season',
        "A hero who's always picked last discovers their own unique way of helping the team win",
    ],
    'Seasons & Holidays': [
        'The first snowfall of winter hides a surprise that only appears once the whole town works together',
        'An autumn leaf collector discovers one leaf that never seems to fall',
        'A spring garden won\'t bloom until the hero learns the secret of patience',
        'A summer bonfire on the beach brings the whole neighbourhood together for one magical night',
        'A missing holiday decoration sets off a heartwarming search around the whole house',
        'The hero learns that the best part of any holiday is the traditions made with people they love',
        'A snowman that seems to come alive each winter has one very important wish',
    ],
};

// Common children's-book morals — suggested chips for the "Moral of the
// story" field. Free text is always available too.
export const MORAL_OPTIONS: string[] = [
    'Kindness', 'Honesty', 'Courage', 'Sharing', 'Perseverance', 'Friendship',
    'Patience', 'Gratitude', 'Forgiveness', 'Teamwork', 'Responsibility', 'Empathy',
    'Curiosity', 'Self-belief', 'Generosity', 'Respect for others', 'Trying new things',
    'Being yourself', 'Helping others', 'Facing your fears',
];

// How a non-main character relates to the main one — picklist + "Other" free
// text, same pattern as the story-type Custom fallback.
export const RELATION_OPTIONS: string[] = [
    'Sibling', 'Friend', 'Pet', 'Parent', 'Grandparent', 'Cousin', 'Classmate', 'Neighbor', 'Other',
];
